import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ClothTypeDef } from '@api/types/ClothTypeDef';
import { CustomerOrderDef } from '@api/types/CustomerOrderDef';
import { CatCustomerOrderDef } from '@api/types/CatCustomerOrderDef';
import { PartnerOrderDef } from '@api/types/PartnerOrderDef';
import { PartnerRequestDef } from '@api/types/PartnerRequestDef';
import { PaymentTypeDef } from '@api/types/PaymentTypeDef';
import { DeliveryTypeDef } from '@api/types/DeliveryTypeDef';
import { CatProbeOrderDef } from '@api/types/CatProbeOrderDef';
import { AnimalTypeEnum } from '@api/types/AnimalTypeEnum';
import { PricingModel } from '@api/types/PricingModel';
import { Region } from '@api/types/Region';
import { PublicAuthToken } from '@api/types/PublicAuthToken';
import { LoginObject } from '@api/types/LoginObject';
import { Article } from '@api/types/Article';
import { OrderResponse } from '@api/types/OrderResponse';
import { AppLanguageCode } from '@project-types/AppLanguageCodes';
import { getLanguageTag } from '@translations/functions';



export const baseApi = createApi({
	reducerPath: 'baseApi',
	baseQuery: fetchBaseQuery({
		baseUrl: `${process.env.DATA_API_URL}/`,
	}),
	tagTypes: [
		'price',
		'clothTypes',
		'paymentTypes',
		'deliveryTypes',
		'catProbePrice',
		'partners',
		'article',
	],
	endpoints: (builder) => ({

		login: builder.mutation<PublicAuthToken,Omit<LoginObject, 'app'>>({
			query: ({ username, password }) => ({
				url: `${process.env.AUTH_SERVER_URL}/User/Login`,
				method: 'POST',
				body: { username, password, app: process.env.AUTH_APP }
			}),
		}),

		getResources: builder.query<string, { language: AppLanguageCode }>({
			query: ({ language }) => ({
				url: 'Resources/GetResources',
				params: { language }
			}),
		}),

		getArticle: builder.query<Article, { id: string }>({
			query: ({ id }) => ({
				url: 'Article/GetSingle',
				params: { id }
			}),
			providesTags: ['article']
		}),
		
		getPrice: builder.query<PricingModel, { size: number, animalType: AnimalTypeEnum, paymentType?: number, deliveryType?: number }>({
			query: ({ size, animalType, paymentType, deliveryType }) => ({
				url: 'Pricing/GetPrice',
				params: { size, animalType, paymentType, deliveryType }
			}),
			providesTags: ['price'],
		}),

		getPriceWithCountry: builder.query<PricingModel, { size: number, animalType: AnimalTypeEnum, paymentType?: number, deliveryType?: number, country: AppLanguageCode }>({
			query: ({ size, animalType, paymentType, deliveryType, country }) => ({
				url: 'Pricing/GetPriceWithCountry',
				params: { size, animalType, paymentType, deliveryType, country }
			}),
			providesTags: ['price'],
		}),

		getCatProbePrice: builder.query<number, { neckCircumference: number }>({
			query: ({ neckCircumference }) => ({
				url: 'Pricing/GetPrice',
				params: { neckCircumference }
			}),
			providesTags: ['catProbePrice'],
		}),

		getClothTypes: builder.query<ClothTypeDef[], { animalType: AnimalTypeEnum }>({
			query: ({ animalType }) => ({
				url: 'ClothType/GetClothTypes',
				params: { animalType }
			}),
			providesTags: ['clothTypes'],
		}),

		getPaymentTypes: builder.query<PaymentTypeDef[], { country: AppLanguageCode }>({
			query: ({ country }) => ({
				url: 'DeliveryType/GetPaymentTypes',
				params: { country }
			}),
			providesTags: ['paymentTypes'],
		}),

		getDeliveryTypes: builder.query<DeliveryTypeDef[], { country: AppLanguageCode }>({
			query: ({ country }) => ({
				url: 'DeliveryType/GetDeliveryTypes',
				params: { country }
			}),
			providesTags: ['deliveryTypes'],
		}),


		getPartnersByCountry: builder.query<Region[], { country: AppLanguageCode }>({
			query: ({ country }) => ({
				url: 'Partner/GetPartnersByCountry',
				params: { country }
			}),
			transformResponse: (response: Region[]) => {
				const collator = new Intl.Collator(getLanguageTag(), { sensitivity: 'base' });
				return response.sort((a, b) => collator.compare(a.name, b.name));
			},
			providesTags: ['partners'],
		}),


		saveCustomerOrder: builder.mutation<OrderResponse, CustomerOrderDef>({
			query: (customerOrder) => ({
				url: 'CustomerOrder/Save',
				method: 'POST',
				body: customerOrder
			}),
		}),

		saveCatCustomerOrder: builder.mutation<OrderResponse, CatCustomerOrderDef>({
			query: (catCustomerOrder) => ({
				url: 'CatCustomerOrder/Save',
				method: 'POST',
				body: catCustomerOrder
			}),
		}),

		savePartnerOrder: builder.mutation<void, PartnerOrderDef>({
			query: (partnerOrder) => ({
				url: 'PartnerOrder/Save',
				method: 'POST',
				body: partnerOrder
			}),
		}),

		savePartnerRequest: builder.mutation<void, PartnerRequestDef>({
			query: (partnerRequest) => ({
				url: 'PartnerRequest/Save',
				method: 'POST',
				body: partnerRequest
			}),
		}),

		saveCatProbeRequest: builder.mutation<void, CatProbeOrderDef>({
			query: (catProbeRequest) => ({
				url: 'CatProbeRequest/Save',
				method: 'POST',
				body: catProbeRequest
			}),
		}),
	}),
});



export const {
	useLoginMutation,
	useGetResourcesQuery,
	useGetArticleQuery,
	useLazyGetArticleQuery,
	useLazyGetPriceQuery,
	useLazyGetPriceWithCountryQuery,
	useLazyGetCatProbePriceQuery,
	useGetClothTypesQuery,
	useLazyGetPaymentTypesQuery,
	useLazyGetDeliveryTypesQuery,
	useGetPartnersByCountryQuery,
	useSaveCustomerOrderMutation,
	useSaveCatCustomerOrderMutation,
	useSavePartnerOrderMutation,
	useSavePartnerRequestMutation,
	useSaveCatProbeRequestMutation } = baseApi;